import {
  type DomainCommandContext,
  DomainFault,
  type DomainReadContext,
} from '../../contracts';
import type { Command } from '../../service';
import type { Entity, Evidence } from '../../types';
import { taskResults } from '../analysis';
import { durationOf, recordingCommand, resolveEvidence } from '../recording';
import { canManageLearning, learningManagers } from './access';
import type {
  ImprovementPlan,
  LearningView,
  TeacherProfile,
  TeachingObservation,
} from './types';
export function fail(text: string, code = 'VALIDATION'): never {
  throw new DomainFault(code, text);
}
export const need = (value: string | undefined, name: string) =>
  value?.trim() || fail(`请填写${name}`);
export function readField<T>(row: Entity, key: string, fallback: T): T {
  if (!row.fields?.[key]) return structuredClone(fallback);
  try {
    return JSON.parse(row.fields[key]);
  } catch {
    return fail('业务资料格式不兼容，请保留数据并联系管理员', 'CONFLICT');
  }
}
export function writeField(row: Entity, key: string, value: unknown) {
  row.fields = { ...row.fields, [key]: JSON.stringify(value) };
}
export function entity(
  ctx: DomainReadContext,
  uuid: string | undefined,
  kind: Entity['kind'],
) {
  const row = ctx.get(uuid);
  if (!row || row.kind !== kind)
    return fail('对象不存在或当前范围无权访问', 'FORBIDDEN');
  return row;
}
export function preview(input: Command) {
  const at = Number(input.fields?.previewAt);
  if (!Number.isFinite(at) || Math.abs(Date.now() - at) > 900000)
    fail('预览已过期，请重新打开并核实', 'CONFLICT');
}
export function assertAuthor(ctx: DomainReadContext, row: Entity) {
  if (!canManageLearning(row, ctx.context))
    fail('仅本人或授权教学管理人员可操作', 'FORBIDDEN');
}
export const observationsFor = (lesson: Entity) =>
  readField<TeachingObservation[]>(lesson, 'learningObservations', []);
export const profileFor = (teacher: Entity): TeacherProfile =>
  readField(teacher, 'learningProfile', {
    subject: teacher.fields?.subject || '待补充',
    topics: [],
    training: [],
  });
export const planFor = (plan: Entity) =>
  readField<ImprovementPlan | undefined>(plan, 'learningPlan', undefined);
export function teacherFor(ctx: DomainReadContext, memberUuid: string) {
  const teacher = ctx.list('teacher').find((t) => t.memberUuid === memberUuid);
  if (!teacher) return fail('教师不在授权目录中', 'FORBIDDEN');
  return teacher;
}
export function safeEvidence(ctx: DomainReadContext, evidence?: Evidence) {
  if (!evidence) return undefined;
  try {
    return resolveEvidence(ctx, evidence);
  } catch {
    return undefined;
  }
}
export function recordingsFor(ctx: DomainReadContext, lessonUuid: string) {
  return ctx
    .list('recording')
    .filter(
      (r) =>
        r.fields?.lessonUuid === lessonUuid &&
        r.fields?.linkStatus === 'confirmed',
    );
}
function reference(evidence: Evidence): Evidence {
  return { ...evidence, quote: '' };
}
function record(
  ctx: DomainCommandContext,
  row: Entity,
  action: string,
  note: string,
) {
  const data = planFor(row);
  if (data) {
    data.history.push({
      uuid: crypto.randomUUID(),
      at: ctx.now,
      action,
      actor: ctx.context.viewMemberUuid,
      note,
    });
    writeField(row, 'learningPlan', data);
  }
  ctx.touch(row);
  ctx.notify(row, action);
}
function validateEvidenceForLesson(
  ctx: DomainReadContext,
  lesson: Entity,
  input: Evidence,
) {
  const recording = entity(ctx, input?.recordingUuid, 'recording');
  if (
    recording.fields?.lessonUuid !== lesson.uuid ||
    recording.fields?.linkStatus !== 'confirmed' ||
    recording.memberUuid !== lesson.memberUuid ||
    recording.campus !== lesson.campus
  )
    fail('证据必须属于该教师已确认关联的课次');
  return resolveEvidence(ctx, input);
}
export function learningQuery(ctx: DomainReadContext): LearningView {
  const lessons = ctx.list('lesson');
  const recordings = ctx.list('recording');
  const teachers = ctx
    .list('teacher')
    .filter(
      (t) =>
        learningManagers.includes(ctx.context.role) ||
        t.memberUuid === ctx.context.viewMemberUuid ||
        profileFor(t).mentorMemberUuid === ctx.context.viewMemberUuid,
    );
  const plans = ctx.list('plan');
  const observations = lessons.flatMap(observationsFor).flatMap((o) => {
    const evidence = safeEvidence(ctx, o.evidence);
    return evidence ? [{ ...o, evidence }] : [];
  });
  const candidates: LearningView['candidates'] = [];
  for (const job of ctx.list('job').filter((j) => j.fields?.analysisTask)) {
    try {
      for (const result of taskResults(ctx, job))
        for (const candidate of result.suggestions.filter(
          (c) => c.category === 'teaching',
        )) {
          if (!observations.some((o) => o.candidateUuid === candidate.uuid))
            candidates.push({
              candidateUuid: candidate.uuid,
              jobUuid: job.uuid,
              text: candidate.observation,
              topic: candidate.title,
              evidence: resolveEvidence(ctx, candidate.evidence),
              memberUuid: result.input.memberUuid,
              campus: result.input.campus,
            });
        }
    } catch {
      /* Inaccessible source contributes neither text nor counts. */
    }
  }
  const managedPlans = plans.filter(
    (p) => p.fields?.learningManaged === 'true',
  );
  return {
    lessons,
    recordings,
    teachers,
    plans,
    observations,
    candidates,
    metrics: {
      lessons: lessons.length,
      recordedLessons: lessons.filter((l) =>
        recordings.some(
          (r) =>
            r.fields?.lessonUuid === l.uuid &&
            r.fields?.linkStatus === 'confirmed',
        ),
      ).length,
      confirmedObservations: observations.filter(
        (o) => o.status === 'confirmed',
      ).length,
      pendingObservations:
        observations.filter((o) => o.status === 'candidate').length +
        candidates.length,
      plans: managedPlans.length,
      completedPlans: managedPlans.filter((p) => p.status === 'completed')
        .length,
    },
  };
}
export interface LessonSection {
  uuid: string;
  recordingUuid: string;
  transcriptVersionUuid: string;
  startMs: number;
  endMs: number;
  title: string;
  role: 'teacher' | 'student' | 'interaction' | 'unknown';
  confirmed: boolean;
}
export function sectionsFor(lesson: Entity) {
  return readField<LessonSection[]>(lesson, 'learningSections', []);
}
export function lessonStructure(ctx: DomainReadContext, lesson: Entity) {
  const recordings = recordingsFor(ctx, lesson.uuid);
  const totalMs = recordings.reduce((n, r) => n + durationOf(r), 0);
  const sections = sectionsFor(lesson).filter((s) => {
    const r = recordings.find((r) => r.uuid === s.recordingUuid);
    return r && r.versions?.at(-1)?.uuid === s.transcriptVersionUuid;
  });
  const confirmed = sections.filter((s) => s.confirmed && s.role !== 'unknown');
  const knownMs = confirmed.reduce((n, s) => n + s.endMs - s.startMs, 0);
  return {
    totalMs,
    knownMs,
    unknownMs: Math.max(0, totalMs - knownMs),
    sections,
    teacherMs: confirmed
      .filter((s) => s.role === 'teacher')
      .reduce((n, s) => n + s.endMs - s.startMs, 0),
    studentMs: confirmed
      .filter((s) => s.role === 'student')
      .reduce((n, s) => n + s.endMs - s.startMs, 0),
    complete: totalMs > 0 && knownMs === totalMs,
  };
}
export function learningCommand(
  ctx: DomainCommandContext,
  input: Command,
): Entity {
  preview(input);
  const fields = input.fields || {};
  const op = input.action.split('.').at(-1);
  if (op === 'saveLesson') {
    const row = input.uuid ? entity(ctx, input.uuid, 'lesson') : undefined;
    const memberUuid = need(input.memberUuid, '授课教师');
    const teacher = teacherFor(ctx, memberUuid);
    if (
      ctx.context.role === 'teacher' &&
      memberUuid !== ctx.context.viewMemberUuid
    )
      fail('不能代其他教师创建课次', 'FORBIDDEN');
    if (row) {
      assertAuthor(ctx, row);
      if (
        recordingsFor(ctx, row.uuid).length &&
        (row.memberUuid !== memberUuid || row.campus !== teacher.campus)
      )
        fail('已有录音的课次不能静默更换教师或校区');
    }
    const startAt = need(input.startAt, '开始时间');
    const endAt = need(fields.endAt, '结束时间');
    if (
      !Number.isFinite(Date.parse(startAt)) ||
      !Number.isFinite(Date.parse(endAt)) ||
      Date.parse(endAt) <= Date.parse(startAt)
    )
      fail('课次结束时间必须晚于开始时间');
    const extra = {
      memberUuid,
      campus: teacher.campus,
      startAt,
      endAt,
      fields: {
        ...row?.fields,
        courseName: need(fields.courseName, '课程名称'),
        subject: need(fields.subject, '学科'),
        className: need(fields.className, '班级'),
        goals: need(fields.goals, '课程目标'),
        learningManaged: 'true',
      },
    };
    if (row) {
      Object.assign(row, extra, { title: need(input.title, '课次名称') });
      ctx.touch(row);
      return row;
    }
    return ctx.append('lesson', need(input.title, '课次名称'), {
      ...extra,
      status: 'scheduled',
    });
  }
  if (op === 'linkRecording') {
    const lesson = entity(ctx, fields.lessonUuid, 'lesson');
    assertAuthor(ctx, lesson);
    if (String(lesson.revision) !== fields.lessonRevision)
      fail('课次资料已变化，请重新预览', 'CONFLICT');
    const recording = entity(ctx, input.uuid, 'recording');
    if (
      recording.memberUuid !== lesson.memberUuid ||
      recording.campus !== lesson.campus
    )
      fail('录音与课次教师及校区必须一致');
    if (
      (!lesson.startAt ||
        !lesson.endAt ||
        !recording.startAt ||
        !recording.endAt ||
        Date.parse(recording.endAt) <= Date.parse(lesson.startAt) ||
        Date.parse(recording.startAt) >= Date.parse(lesson.endAt)) &&
      fields.ackConflict !== 'true'
    )
      fail('课次与录音时间不匹配或信息缺失，需人工确认冲突');
    return recordingCommand(ctx, {
      ...input,
      action: 'recording.associate',
      fields: { ...fields, decision: 'confirm' },
    });
  }
  if (op === 'saveSection') {
    const lesson = entity(ctx, input.uuid, 'lesson');
    assertAuthor(ctx, lesson);
    const recording = entity(ctx, fields.recordingUuid, 'recording');
    const evidence = validateEvidenceForLesson(ctx, lesson, {
      recordingUuid: recording.uuid,
      transcriptVersionUuid: recording.versions?.at(-1)?.uuid || '',
      startMs: Number(fields.startMs),
      endMs: Number(fields.endMs),
      quote: '',
    });
    const role = fields.role as LessonSection['role'];
    if (!['teacher', 'student', 'interaction', 'unknown'].includes(role))
      fail('请选择明确角色或未知');
    if (role !== 'unknown' && fields.confirmed !== 'true')
      fail('身份与章节角色未经人工确认，不能计算结构');
    const sections = sectionsFor(lesson);
    if (
      sections.some(
        (s) =>
          s.recordingUuid === recording.uuid &&
          s.startMs < evidence.endMs &&
          s.endMs > evidence.startMs,
      )
    )
      fail('章节时间范围重叠，请按未分配区间整理');
    sections.push({
      uuid: crypto.randomUUID(),
      ...reference(evidence),
      title: need(input.title, '章节名称'),
      role,
      confirmed: fields.confirmed === 'true',
    });
    writeField(lesson, 'learningSections', sections);
    ctx.touch(lesson);
    return lesson;
  }
  if (op === 'observe' || op === 'confirmObservation') {
    const lesson = entity(ctx, input.uuid, 'lesson');
    assertAuthor(ctx, lesson);
    const observations = observationsFor(lesson);
    if (op === 'observe') {
      const evidence = validateEvidenceForLesson(
        ctx,
        lesson,
        input.evidence as Evidence,
      );
      let text = need(input.note, '教学观察');
      let topic = need(input.title, '成长主题');
      let source: 'manual' | 'analysis' = 'manual';
      if (fields.candidateUuid) {
        const job = entity(ctx, fields.jobUuid, 'job');
        const candidate = taskResults(ctx, job)
          .flatMap((r) => r.suggestions)
          .find(
            (c) => c.uuid === fields.candidateUuid && c.category === 'teaching',
          );
        if (
          !candidate ||
          candidate.evidence.recordingUuid !== evidence.recordingUuid ||
          candidate.evidence.transcriptVersionUuid !==
            evidence.transcriptVersionUuid ||
          candidate.evidence.startMs !== evidence.startMs ||
          candidate.evidence.endMs !== evidence.endMs
        )
          fail('教学候选或证据版本不匹配');
        if (observations.some((o) => o.candidateUuid === candidate.uuid))
          return lesson;
        text = candidate.observation;
        topic = candidate.title;
        source = 'analysis';
      }
      observations.push({
        uuid: crypto.randomUUID(),
        lessonUuid: lesson.uuid,
        teacherMemberUuid: lesson.memberUuid,
        topic,
        text,
        evidence: reference(evidence),
        source,
        candidateUuid: fields.candidateUuid,
        analysisTaskUuid: fields.jobUuid,
        status: 'candidate',
        createdAt: ctx.now,
      });
    } else {
      const observation = observations.find(
        (o) => o.uuid === fields.observationUuid,
      );
      if (!observation || observation.status !== 'candidate')
        fail('观察不存在或已被确认', 'CONFLICT');
      validateEvidenceForLesson(ctx, lesson, observation.evidence);
      if (!['confirmed', 'excluded'].includes(fields.decision))
        fail('请选择确认或排除');
      Object.assign(observation, {
        status: fields.decision,
        reason: need(input.note, '确认依据'),
        confirmedAt: ctx.now,
        confirmedBy: ctx.context.viewMemberUuid,
      });
    }
    writeField(lesson, 'learningObservations', observations);
    ctx.touch(lesson);
    return lesson;
  }
  if (op === 'assignMentor' || op === 'saveProfile' || op === 'training') {
    const teacher = entity(ctx, input.uuid, 'teacher');
    const profile = profileFor(teacher);
    if (op === 'assignMentor') {
      if (!learningManagers.includes(ctx.context.role))
        fail('仅授权教学管理人员可安排指导关系', 'FORBIDDEN');
      const mentor = ctx
        .members()
        .find(
          (m) =>
            m.memberUuid === fields.mentorMemberUuid &&
            m.campus === teacher.campus,
        );
      if (!mentor || mentor.memberUuid === teacher.memberUuid)
        fail('导师必须为同校区的另一位目录成员');
      profile.mentorMemberUuid = mentor.memberUuid;
      profile.mentorName = mentor.title;
      need(input.note, '指导安排依据');
    } else {
      assertAuthor(ctx, teacher);
      if (op === 'saveProfile') {
        profile.subject = need(fields.subject, '任教学科');
        profile.topics = need(fields.topics, '成长主题')
          .split(/[，,]/)
          .map((v) => v.trim())
          .filter(Boolean);
      } else {
        profile.training.push({
          uuid: crypto.randomUUID(),
          title: need(input.title, '培训主题'),
          at: ctx.now,
          feedback: need(input.note, '培训反馈'),
        });
      }
    }
    writeField(teacher, 'learningProfile', profile);
    ctx.touch(teacher);
    return teacher;
  }
  if (op === 'createPlan') {
    const lesson = entity(ctx, input.uuid, 'lesson');
    assertAuthor(ctx, lesson);
    const observation = observationsFor(lesson).find(
      (o) => o.uuid === fields.observationUuid && o.status === 'confirmed',
    );
    if (!observation) fail('仅已确认教学观察可建立正式计划');
    const existing = ctx
      .list('plan')
      .find((p) => planFor(p)?.observationUuid === observation.uuid);
    if (existing) return existing;
    const evidence = validateEvidenceForLesson(
      ctx,
      lesson,
      observation.evidence,
    );
    const teacher = teacherFor(ctx, lesson.memberUuid);
    const profile = profileFor(teacher);
    const mentorUuid = need(fields.mentorMemberUuid, '导师');
    const mentor = ctx
      .members()
      .find((m) => m.memberUuid === mentorUuid && m.campus === lesson.campus);
    if (
      mentorUuid === lesson.memberUuid ||
      (!mentor && profile.mentorMemberUuid !== mentorUuid)
    )
      fail('导师须为同校区授权成员且不能是本人');
    const dueAt = need(input.dueAt, '计划期限');
    if (
      !Number.isFinite(Date.parse(dueAt)) ||
      Date.parse(dueAt) <= Date.parse(ctx.now)
    )
      fail('计划期限应晚于当前业务时间');
    const plan = ctx.append('plan', need(input.title, '计划名称'), {
      status: 'pending',
      campus: lesson.campus,
      memberUuid: lesson.memberUuid,
      owner: mentorUuid,
      dueAt,
      sourceUuid: lesson.uuid,
      fields: {
        learningManaged: 'true',
        recordingUuid: evidence.recordingUuid,
        authorView: ctx.context.viewMemberUuid,
      },
    });
    const data: ImprovementPlan = {
      schema: 1,
      goal: need(fields.goal, '可观察目标'),
      topic: observation.topic,
      successCriteria: need(fields.successCriteria, '完成条件'),
      observationUuid: observation.uuid,
      sourceLessonUuid: lesson.uuid,
      evidence: reference(evidence),
      mentorMemberUuid: mentorUuid,
      mentorName: mentor?.title || profile.mentorName || '已授权导师',
      practices: [],
      reviews: [],
      history: [
        {
          uuid: crypto.randomUUID(),
          at: ctx.now,
          actor: ctx.context.viewMemberUuid,
          action: '创建计划（待接受）',
          note: need(input.note, '采纳依据'),
        },
      ],
      references: [],
    };
    const task = ctx.append('task', `${plan.title} · 接受与实践`, {
      status: 'pending',
      campus: plan.campus,
      memberUuid: plan.memberUuid,
      owner: plan.memberUuid,
      dueAt,
      sourceUuid: plan.uuid,
      fields: { taskType: 'learningPractice', target: '/teaching/plan' },
    });
    data.taskUuid = task.uuid;
    writeField(plan, 'learningPlan', data);
    ctx.notify(plan, '教学改进计划待接受');
    return plan;
  }
  const plan = entity(ctx, input.uuid, 'plan');
  const data = planFor(plan);
  if (!data)
    fail('此为历史或上游草稿，尚无正式实践证据；请从已确认观察建立计划');
  const teacherOrManager = canManageLearning(plan, ctx.context);
  const mentor = ctx.context.viewMemberUuid === data.mentorMemberUuid;
  const note = need(input.note, '操作依据');
  let action = '';
  if (op === 'acceptPlan') {
    if (!teacherOrManager || plan.status !== 'pending')
      fail('只有待接受计划可由教师接受', 'INVALID_TRANSITION');
    plan.status = 'inProgress';
    action = '接受计划';
  } else if (op === 'schedulePractice') {
    if (!teacherOrManager || plan.status !== 'inProgress')
      fail('请先接受计划再安排实践', 'INVALID_TRANSITION');
    const lesson = entity(ctx, fields.lessonUuid, 'lesson');
    if (
      lesson.memberUuid !== plan.memberUuid ||
      lesson.campus !== plan.campus ||
      lesson.uuid === data.sourceLessonUuid
    )
      fail('实践须安排另一堂同教师同校区课次');
    data.scheduledLessonUuid = lesson.uuid;
    action = '安排下次实践';
  } else if (op === 'submitPractice') {
    if (
      !teacherOrManager ||
      plan.status !== 'inProgress' ||
      !data.scheduledLessonUuid
    )
      fail('请先安排实践课次', 'INVALID_TRANSITION');
    const lesson = entity(ctx, data.scheduledLessonUuid, 'lesson');
    const evidence = validateEvidenceForLesson(
      ctx,
      lesson,
      input.evidence as Evidence,
    );
    if (evidence.recordingUuid === data.evidence.recordingUuid)
      fail('实践须使用另一堂课的独立录音，不能重复原始片段');
    if (fields.redacted !== 'true') fail('请确认实践反馈已脱敏');
    data.practices.push({
      uuid: crypto.randomUUID(),
      planUuid: plan.uuid,
      lessonUuid: lesson.uuid,
      evidence: reference(evidence),
      report: need(fields.report, '实际实践材料与反馈'),
      conditions: need(fields.conditions, '样本条件与差异'),
      createdAt: ctx.now,
      submittedBy: ctx.context.viewMemberUuid,
      accepted: false,
    });
    plan.status = 'pendingFeedback';
    action = '提交实践材料';
  } else if (op === 'reviewPractice') {
    if (!mentor || ctx.context.viewMemberUuid === plan.memberUuid)
      fail('仅指定的另一位导师可验收', 'FORBIDDEN');
    if (plan.status !== 'pendingFeedback')
      fail('没有待验收实践', 'INVALID_TRANSITION');
    const practice = data.practices.at(-1);
    if (
      !practice ||
      !practice.report ||
      !safeEvidence(ctx, practice.evidence) ||
      !safeEvidence(ctx, data.evidence)
    )
      fail('实践或原始证据不可读，不能代替验收；请申请来源授权');
    if (
      fields.decision === 'accept' &&
      [data.evidence, practice.evidence].some(
        (e) =>
          ctx.get(e.recordingUuid)?.versions?.at(-1)?.uuid !==
          e.transcriptVersionUuid,
      )
    )
      fail('原课堂或实践转写已有新版本，请重核材料后再验收', 'CONFLICT');
    if (!['accept', 'return'].includes(fields.decision))
      fail('请选择验收或退回');
    const accepted = fields.decision === 'accept';
    data.reviews.push({
      uuid: crypto.randomUUID(),
      practiceUuid: practice.uuid,
      reviewer: ctx.context.viewMemberUuid,
      at: ctx.now,
      accepted,
      note,
    });
    practice.accepted = accepted;
    plan.status = accepted ? 'completed' : 'inProgress';
    action = accepted ? '导师验收完成' : '导师退回补充';
  } else if (op === 'reopenPlan') {
    if (!mentor && !learningManagers.includes(ctx.context.role))
      fail('仅导师或教学管理人员可重开', 'FORBIDDEN');
    if (plan.status !== 'completed')
      fail('仅已验收计划可重开', 'INVALID_TRANSITION');
    plan.status = 'inProgress';
    action = '重开计划，保留原验收';
  } else if (op === 'cancelPlan') {
    if (!teacherOrManager || ['completed', 'cancelled'].includes(plan.status))
      fail('当前计划不能取消', 'INVALID_TRANSITION');
    plan.status = 'cancelled';
    action = '取消未完成计划';
  } else return fail('未知教学操作');
  writeField(plan, 'learningPlan', data);
  const task = data.taskUuid ? ctx.get(data.taskUuid) : undefined;
  if (task) {
    task.status =
      plan.status === 'completed'
        ? 'completed'
        : plan.status === 'cancelled'
          ? 'cancelled'
          : plan.status === 'pendingFeedback'
            ? 'pendingAcceptance'
            : 'inProgress';
    task.owner =
      plan.status === 'pendingFeedback'
        ? data.mentorMemberUuid
        : plan.memberUuid;
    ctx.touch(task);
  }
  record(ctx, plan, action, note);
  return plan;
}
